<!--
  REV-21369 — abcp-pdf-viewer (ADR-0021). Contract: e2e-lab/pdf-viewer.lab.spec.ts.

  Test-Marken (Tests hängen an diesen, nie an Farben, Pixeln oder Prosa):
    [data-testid="pdf-viewer"]                 Wurzel, [data-state] = loading | error | empty | opening | ready
    [data-testid="pdf-viewer-page"]            eine Seite, [data-page], [data-rotation], [data-rendered]
    [data-testid="pdf-viewer-highlight"]       ein Rechteck einer Markierung, [data-note-id], [data-author], [data-active]
    [data-testid="pdf-viewer-pending-mark"]    ein Rechteck der Markierung, die gerade kommentiert wird
    [data-testid="pdf-viewer-mark-action"]     „Markieren & kommentieren" am Entwurf
    [data-testid="pdf-viewer-mode-text|area"]  Markier-Modus, [aria-pressed]
    [data-testid="pdf-viewer-download"]        Herunterladen (Freigabe Q3: kein Drucken)
-->
<div class="abcp-pdf" data-testid="pdf-viewer" [attr.data-state]="viewState" [attr.data-mode]="canAnnotate ? mode : null">
    <div class="abcp-pdf__toolbar" data-testid="pdf-viewer-toolbar" *ngIf="viewState === 'ready'">
        <div class="abcp-pdf__group">
            <button
                type="button"
                class="abcp-pdf__btn"
                data-testid="pdf-viewer-prev"
                [disabled]="currentPage <= 1"
                [attr.aria-label]="'abcp_pdf_viewer_prev_page' | i18next"
                (click)="previousPage()"
            >
                <lucide-icon name="chevron-left" [size]="14" aria-hidden="true"></lucide-icon>
            </button>
            <span class="abcp-pdf__label" data-testid="pdf-viewer-page-label" [attr.data-page]="currentPage" [attr.data-total]="pages.length">
                {{ 'abcp_pdf_viewer_page_of' | i18next: { page: currentPage, total: pages.length } }}
            </span>
            <button
                type="button"
                class="abcp-pdf__btn"
                data-testid="pdf-viewer-next"
                [disabled]="currentPage >= pages.length"
                [attr.aria-label]="'abcp_pdf_viewer_next_page' | i18next"
                (click)="nextPage()"
            >
                <lucide-icon name="chevron-right" [size]="14" aria-hidden="true"></lucide-icon>
            </button>
        </div>

        <div class="abcp-pdf__group">
            <button type="button" class="abcp-pdf__btn" data-testid="pdf-viewer-zoom-out" [attr.aria-label]="'abcp_pdf_viewer_zoom_out' | i18next" (click)="zoomOut()">
                <lucide-icon name="minus" [size]="14" aria-hidden="true"></lucide-icon>
            </button>
            <span class="abcp-pdf__label abcp-pdf__label--zoom" data-testid="pdf-viewer-zoom" [attr.data-zoom]="zoomPercent">{{ zoomPercent }} %</span>
            <button type="button" class="abcp-pdf__btn" data-testid="pdf-viewer-zoom-in" [attr.aria-label]="'abcp_pdf_viewer_zoom_in' | i18next" (click)="zoomIn()">
                <lucide-icon name="plus" [size]="14" aria-hidden="true"></lucide-icon>
            </button>
            <button type="button" class="abcp-pdf__btn abcp-pdf__btn--text" data-testid="pdf-viewer-fit" (click)="fitWidth()">
                {{ 'abcp_pdf_viewer_fit_width' | i18next }}
            </button>
        </div>

        <div class="abcp-pdf__group" *ngIf="canAnnotate" role="group" [attr.aria-label]="'abcp_pdf_viewer_mode' | i18next">
            <button
                type="button"
                class="abcp-pdf__btn abcp-pdf__btn--text"
                data-testid="pdf-viewer-mode-text"
                [attr.aria-pressed]="mode === 'text'"
                (click)="setMode('text')"
            >
                {{ 'abcp_pdf_viewer_mode_text' | i18next }}
            </button>
            <button
                type="button"
                class="abcp-pdf__btn abcp-pdf__btn--text"
                data-testid="pdf-viewer-mode-area"
                [attr.aria-pressed]="mode === 'area'"
                (click)="setMode('area')"
            >
                {{ 'abcp_pdf_viewer_mode_area' | i18next }}
            </button>
        </div>

        <div class="abcp-pdf__group abcp-pdf__group--end" *ngIf="canDownload">
            <button type="button" class="abcp-pdf__btn abcp-pdf__btn--text" data-testid="pdf-viewer-download" (click)="downloadRequested.emit()">
                {{ 'abcp_pdf_viewer_download' | i18next }}
            </button>
        </div>
    </div>

    <div class="abcp-pdf__state" data-testid="pdf-viewer-loading" *ngIf="viewState === 'loading' || viewState === 'opening'">
        {{ 'abcp_pdf_viewer_loading' | i18next }}
    </div>
    <div class="abcp-pdf__state abcp-pdf__state--error" data-testid="pdf-viewer-error" role="alert" *ngIf="viewState === 'error'">
        {{ 'abcp_pdf_viewer_error' | i18next }}
    </div>

    <div
        #scroller
        class="abcp-pdf__scroller"
        data-testid="pdf-viewer-pages"
        [class.abcp-pdf__scroller--area]="canAnnotate && mode === 'area'"
        [class.abcp-pdf__scroller--drafted]="draft !== null"
        [class.abcp-pdf__scroller--zooming]="zooming"
        [attr.aria-label]="fileName"
        (mouseup)="onPointerUp($event)"
        (scroll)="onScroll()"
    >
        <div
            *ngFor="let page of viewState === 'ready' ? pages : []; trackBy: trackPage"
            class="abcp-pdf-page"
            data-testid="pdf-viewer-page"
            [attr.data-page]="page.number"
            [attr.data-rotation]="page.rotation"
            [style.width.px]="page.baseWidth * scale"
            [style.height.px]="page.baseHeight * scale"
            (mousedown)="onPagePointerDown($event, page)"
        >
            <div class="abcp-pdf-page__render"></div>

            <div class="abcp-pdf-page__marks">
                <ng-container *ngFor="let mark of marksOf(page.number); trackBy: trackMark">
                    <div
                        *ngFor="let box of mark.boxes"
                        class="abcp-pdf-mark"
                        data-testid="pdf-viewer-highlight"
                        role="button"
                        tabindex="0"
                        [attr.data-note-id]="mark.id"
                        [attr.data-author]="mark.author"
                        [attr.data-active]="mark.id === activeHighlightId ? '' : null"
                        [class.abcp-pdf-mark--active]="mark.id === activeHighlightId"
                        [class.abcp-pdf-mark--ai]="mark.author === 'ai'"
                        [attr.aria-label]="(mark.author === 'ai' ? 'abcp_pdf_viewer_highlight_ai' : 'abcp_pdf_viewer_highlight') | i18next"
                        [style.left.px]="box.left"
                        [style.top.px]="box.top"
                        [style.width.px]="box.width"
                        [style.height.px]="box.height"
                        (mousedown)="$event.stopPropagation()"
                        (click)="selectHighlight(mark.id, $event)"
                        (keydown.enter)="highlightSelected.emit(mark.id)"
                    ></div>
                </ng-container>

                <ng-container *ngIf="pendingBoxes && pendingBoxes.page === page.number">
                    <div
                        *ngFor="let box of pendingBoxes.boxes"
                        class="abcp-pdf-pending"
                        data-testid="pdf-viewer-pending-mark"
                        [style.left.px]="box.left"
                        [style.top.px]="box.top"
                        [style.width.px]="box.width"
                        [style.height.px]="box.height"
                    ></div>
                </ng-container>

                <div
                    *ngIf="rubber && rubber.page === page.number"
                    class="abcp-pdf-rubber abcp-pdf-pending"
                    data-testid="pdf-viewer-rubber"
                    [style.left.px]="rubber.box.left"
                    [style.top.px]="rubber.box.top"
                    [style.width.px]="rubber.box.width"
                    [style.height.px]="rubber.box.height"
                ></div>
            </div>

            <ham-button
                *ngIf="draft && draft.page === page.number"
                class="abcp-pdf-action"
                data-testid="pdf-viewer-mark-action"
                variant="gray"
                size="toolbar"
                [attr.data-kind]="draft.snippet === null ? 'area' : 'text'"
                [style.left.px]="draft.actionLeft"
                [style.top.px]="draft.actionTop"
                (mousedown)="$event.stopPropagation(); $event.preventDefault()"
                (buttonClick)="confirmDraft()"
            >
                {{ 'abcp_pdf_viewer_mark_action' | i18next }}
            </ham-button>
            <!-- The same spot as the start of a Pendenz (account page only). -->
            <ham-button
                *ngIf="todoAction && draft && draft.page === page.number"
                class="abcp-pdf-action"
                data-testid="pdf-viewer-mark-todo"
                variant="gray"
                size="toolbar"
                [style.left.px]="draft.actionLeft"
                [style.top.px]="draft.actionTop + todoActionOffset"
                (mousedown)="$event.stopPropagation(); $event.preventDefault()"
                (buttonClick)="confirmTodoDraft()"
            >
                {{ 'abcp_ledger_mark_todo' | i18next }}
            </ham-button>
        </div>
    </div>
</div>
